import { useTranslation } from 'react-i18next'
import { HiExclamationCircle, HiOutlineClock } from 'react-icons/hi'

import { ApolloError } from '@apollo/client'
import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Circle,
  Flex,
  Icon,
} from '@chakra-ui/react'

import { PendingInviteeIProps } from '../types/types'
import { PendingInvitee } from './PendingInvitee'

const PendingInviteesError = () => {
  const { t } = useTranslation()

  return (
    <AccordionPanel bgColor='transparent' borderTop='1px solid #eaedf3' p='0'>
      <Flex
        alignItems='center'
        color='red.500'
        gap={2}
        justifyContent='center'
        px='4'
        py='6'
      >
        <Icon as={HiExclamationCircle} boxSize='20px' />
        <Box>{t('settings:pending_invitees_error')}</Box>
      </Flex>
    </AccordionPanel>
  )
}

const PendingInviteesEmpty = ({ filter }: { filter: string }) => {
  const { t } = useTranslation()

  if (filter !== '') {
    return (
      <AccordionPanel bgColor='transparent' borderTop='1px solid #eaedf3' p='0'>
        <Box px='4' py='6' textAlign='center'>
          {t('settings:pending_invitees_empty_filtered')}{' '}
          <Box as='span' fontStyle='italic' fontWeight='bold'>
            {filter}
          </Box>
        </Box>
      </AccordionPanel>
    )
  }
  return (
    <AccordionPanel bgColor='transparent' borderTop='1px solid #eaedf3' p='0'>
      <Box px='4' py='6' textAlign='center'>
        {t('settings:pending_invitees_empty')}
      </Box>
    </AccordionPanel>
  )
}

interface PendingInviteesGroupIProps {
  pendingInvitees: PendingInviteeIProps[]
  filter: string
  error?: ApolloError
  resendingId?: string | null
  onResend: (invitee: PendingInviteeIProps) => void
  onRemove: (invitee: PendingInviteeIProps) => void
}

export const PendingInviteesGroup = ({
  pendingInvitees,
  filter,
  error,
  resendingId,
  onResend,
  onRemove,
}: PendingInviteesGroupIProps) => {
  const { t } = useTranslation()
  // Filter pending invitees based on name or email
  const filteredInvitees = pendingInvitees.filter((invitee) => {
    if (!filter) return true
    const lowerFilter = filter.toLowerCase()
    const fullName = `${invitee.firstName} ${invitee.lastName}`.toLowerCase()
    const email = invitee.email.toLowerCase()
    return fullName.includes(lowerFilter) || email.includes(lowerFilter)
  })

  const count = filteredInvitees.length

  return (
    <AccordionItem
      _hover={{
        boxShadow: count === 0 ? 'base' : '0px 2px 5px 6px rgba(0, 0, 0, 0.15)',
      }}
      border='none'
      borderRadius='5px'
      boxShadow='0px 2px 6px 2px rgba(0, 0, 0, 0.1)'
      mb='6'
      overflow='hidden'
      p='0'
      transition='ease-in-out'
      transitionDuration='300ms'
      transitionProperty='all'
    >
      <h2 style={{ position: 'relative' }}>
        <AccordionButton
          _focus={{ boxShadow: 'none' }}
          bg='white'
          data-testid='settingsPage_managedAccounts_sectionPendingInviteesGroupButton'
          p='4'
        >
          <Flex alignItems='center' flex='1' gap={2}>
            <Icon as={HiOutlineClock} boxSize='20px' color='orange.500' />
            <Box
              fontSize='20px'
              fontWeight='extrabold'
              letterSpacing='-0.6px'
              textAlign='left'
            >
              {t('settings:pending_invitees_section_title')}
            </Box>
          </Flex>
          <Flex alignItems='center'>
            <Circle
              bg='orange.500'
              color='white'
              data-testid='settingsPage_managedAccounts_countPendingInvitees'
              fontSize='14px'
              fontWeight='bold'
              mr='3'
              size='20px'
            >
              {count}
            </Circle>
            <AccordionIcon />
          </Flex>
        </AccordionButton>
      </h2>
      {error ? (
        <PendingInviteesError />
      ) : count > 0 ? (
        filteredInvitees.map((invitee, index) => (
          <PendingInvitee
            index={index}
            invitee={invitee}
            isResending={invitee.id === resendingId}
            key={invitee.id}
            onRemove={onRemove}
            onResend={onResend}
          />
        ))
      ) : (
        <PendingInviteesEmpty filter={filter} />
      )}
    </AccordionItem>
  )
}
